import { TAPIPayload } from '@myua/api';
import { differenceBy, intersectionBy, partition, zipWith } from 'lodash';
import Notifications from 'react-notification-system-redux';
import { api } from '../../../general/apiScenarios';
import { INode, Tag } from '../../../general/model';
import { CHNode, CHNodeIsFolder } from '../../../general/node';
import { TSagaReturnSuccess } from '../../../general/scenarioFactory';
import { nonUndefined } from '../../../general/utils/nonNull';
import { ListenerApiLight, _select, _take, lm } from '../../listenerMiddleware';
import { logger } from '../../logger';
import * as fileManagerActions from '../fileManager/actions';
import { getSelectedNodesInAllFolders } from '../fileManager/selectors/selectors';
import * as tagActions from './actions';
import { Messages, TagShape } from './model';
import { getTag } from './selectors';

export function tagRootListener() {
  watchTagsButtonClicked();
  watchApplyButtonClicked();
}

const getCommonTags = (snodes: { tags: Tag[] }[]): Tag[] => {
  const nodesTags = snodes.map((x) => x.tags);
  return intersectionBy(...nodesTags, (tag: Tag) => tag.id);
};

export async function tagWindow(
  lapi: ListenerApiLight,
  nodes: (INode | CHNode)[],
): Promise<boolean> {
  // Открываем и инициализиручшем окно
  lapi.dispatch(tagActions.setIsOpen({ isOpen: true }));

  const response = await api.tags.load();
  if (lapi.signal.aborted) return false;
  if (!response.success) {
    lapi.dispatch(tagActions.setIsOpen({ isOpen: false }));
    lapi.dispatch(Notifications.error(Messages.initModalError()));
    return false;
  }

  lapi.dispatch(tagActions.modalInitialized({ tagsLoaded: response.data.response }));

  // В дропдауне выбираем теги, которые присутствуют во всех выбранных нодах
  const commonTags = getCommonTags(nodes);

  lapi.dispatch(tagActions.tagsSelected({ tags: commonTags }));
  // ждем юзера
  const action = await _take(lapi, tagActions.modalActionButtonClicked);
  if (lapi.signal.aborted) return false;
  if (action.payload.button === 'cancel') {
    lapi.dispatch(tagActions.setIsOpen({ isOpen: false }));
    return false;
  }

  lapi.dispatch(tagActions.setIsApplying({ isApplying: true }));
  const tagShape: TagShape = _select(lapi, getTag);

  // Среди выбранных тегов могут быть как уже существующие на сервере,
  // так и новые (они введены юзером и имеют временный отрицательный id).
  const [userAddedTagsTemp, existingTags] = partition(tagShape.tagsSelected, (tag) => tag.id < 0);

  // Сохраняем новые теги на сервере, их id становятся нормальными.

  const userAddedTags: Tag[] =
    userAddedTagsTemp.length > 0 ? await saveNewTags(lapi, userAddedTagsTemp) : [];

  if (lapi.signal.aborted) return false;
  // список тегов, которые выбрал пользователь, с уже записанными на бекенде новыми тегами
  const targetTags = [...existingTags, ...userAddedTags];
  // теги, которые общие для всех нод

  // Применяем теги к нодам
  const patchResult = await patchNodeTags(lapi, nodes, commonTags, targetTags, tagShape.recursive);

  if (lapi.signal.aborted) return false;

  lapi.dispatch(tagActions.setIsApplying({ isApplying: false }));

  lapi.dispatch(tagActions.setIsOpen({ isOpen: false }));
  lapi.dispatch(fileManagerActions.allDeselected({}));
  return patchResult;
}

function watchTagsButtonClicked() {
  lm.takeLatest(tagActions.tagsButtonClicked, async (_, lapi) => {
    const selectedNodes: ReturnType<typeof getSelectedNodesInAllFolders> = _select(
      lapi,
      getSelectedNodesInAllFolders,
    );
    await tagWindow(lapi, selectedNodes);
  });
}

export function watchApplyButtonClicked() {
  lm.takeLatest(tagActions.modalActionButtonClicked, function () {});
}

async function saveNewTags(lapi: ListenerApiLight, tags: Tag[]) {
  const results = await Promise.all(tags.map((tag) => api.tags.create(tag.name, lapi.signal)));
  // TODO: throw here instead
  if (lapi.signal.aborted) return [];
  const pairs = zipWith(tags, results, (tag, result) => ({ tag, result }));
  const [goodPairs, badPairs] = partition(
    pairs,
    (pair): pair is { result: TSagaReturnSuccess<TAPIPayload<Tag>>; tag: Tag } =>
      pair.result.success,
  );

  for (const pair of goodPairs) {
    const newTag = pair.result.data.response;
    logger.lapi(lapi, {
      action: 'Tag',
      sub_action: 'Create',
      extra: { text_1: newTag.name, int_1: newTag.id },
    });
  }
  if (badPairs.length > 0) {
    const names = badPairs.map((pair) => pair.tag.name);
    lapi.dispatch(Notifications.error(Messages.createTagsError(names)));
  }

  const createdTags = goodPairs.map(
    (pair) => (pair.result as TSagaReturnSuccess<TAPIPayload<Tag>>).data.response,
  );
  return createdTags;
}

async function patchNodeTags(
  lapi: ListenerApiLight,
  nodes: (INode | CHNode)[],
  commonTags: Tag[],
  newCommonTags: Tag[],
  recursive: boolean,
): Promise<boolean> {
  async function patch(node: CHNode | INode) {
    // все теги этой ноды
    const ownTags = node.tags;
    // теги этой ноды за исключением тех, которые могли быть отредактированы (общие для всех)
    const unaffectedTags = differenceBy(ownTags, commonTags, (tag) => tag.id);
    // новые теги этой ноды = уникальные + список отредактированных
    const newOwnTags = [...unaffectedTags, ...newCommonTags];
    const newOwnTagIDs = newOwnTags.map((x) => x.id);

    const result = await api.tags.apply(node.id, newOwnTagIDs);
    if (lapi.signal.aborted) return;
    if (result.success) {
      lapi.dispatch(tagActions.nodeTagsPatched({ nodeID: node.id, tags: newOwnTags }));
    }
    return result;
  }

  async function recursivePatch(
    addedTagIDs: Tag['id'][],
    removedTagIDs: Tag['id'][],
    parentNodeID: number,
  ) {
    // получаем детей ноды
    const childrenResult = await api.fs.loadNodes(parentNodeID);
    if (lapi.signal.aborted) return;
    if (!childrenResult.success) {
      lapi.dispatch(
        Notifications.error({ title: `Failed to load folder contents in [${parentNodeID}]` }),
      );
      return;
    }
    const children = childrenResult.data.response.nodes;
    const childrenFolderIDs = children.filter(CHNodeIsFolder).map((x) => x.id);
    // патчим теги детей
    const results = await Promise.all(
      children.map((child) => {
        const ownTagIDs = child.tags.map((t) => t.id);

        const newOwnTagIDs = differenceBy(
          [...new Set([...addedTagIDs, ...ownTagIDs])],
          removedTagIDs,
          (x) => x,
        );
        return api.tags.apply(child.id, newOwnTagIDs, lapi.signal);
      }),
    );
    if (lapi.signal.aborted) return;
    const pairs = zipWith(children, results, (child, result) => ({ child, result }));
    const [, badPairs] = partition(pairs, (pair) => pair.result.success);

    if (badPairs.length > 0) {
      const nodeNames = badPairs.map((pair) => pair.child.name);
      lapi.dispatch(Notifications.error(Messages.patchNodeTagsError(nodeNames)));
    }
    // вызываем себя на детях-папках
    await Promise.all(
      childrenFolderIDs.map((childID) => recursivePatch(addedTagIDs, removedTagIDs, childID)),
    );
    if (lapi.signal.aborted) return;
  }

  const results = (await Promise.all(nodes.map((node) => patch(node)))).filter(nonUndefined);
  if (lapi.signal.aborted) return false;
  if (recursive) {
    const addedTagIDs = differenceBy(newCommonTags, commonTags, (x) => x.id).map((x) => x.id);
    const removedTagIDs = differenceBy(commonTags, newCommonTags, (x) => x.id).map((x) => x.id);

    //fixme: results of recursivePatch are discarded and not accounted for down below
    await Promise.all(nodes.map((node) => recursivePatch(addedTagIDs, removedTagIDs, node.id)));
    if (lapi.signal.aborted) return false;
  }

  const pairs = zipWith(nodes, results, (node, result) => ({ node, result }));
  const [, badPairs] = partition(pairs, (pair) => pair.result.success);

  if (badPairs.length > 0) {
    const nodeNames = badPairs.map((pair) => pair.node.name);
    lapi.dispatch(Notifications.error(Messages.patchNodeTagsError(nodeNames)));
    return false;
  } else {
    lapi.dispatch(Notifications.success(Messages.patchNodeTagsSuccess()));
    return true;
  }
}
